import { persistAndApplyTerminalTheme } from "./terminal-theme";

const CDN_ORIGIN = "https://cdn.volqan.com";
const MANIFEST_SCHEMA = "volqan.domain-runtime-manifest.v1";
const SNAPSHOT_SCHEMA = "volqan.domain-runtime.v1";
const MARKET_RUNTIME_SCHEMA = "qommerce.market-surface-runtime.v1";

type JsonObject = Record<string, unknown>;

export type DomainRuntimeSnapshot = JsonObject & {
  schemaVersion: typeof SNAPSHOT_SCHEMA;
  tenant: JsonObject & { host?: string | null };
  version: string;
};

const asObject = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : {};

const text = (value: unknown): string => String(value ?? "").trim();

export const applyDomainRuntimeSnapshot = (snapshot: DomainRuntimeSnapshot): void => {
  const config = asObject(snapshot.config);
  const touchKiosk = asObject(config.touchKiosk);
  const terminal = asObject(touchKiosk.terminal);
  const kioskConfig = asObject(touchKiosk.kioskConfig);
  const tenant = asObject(snapshot.tenant);
  const masterdata = asObject(snapshot.masterdata);
  const theme = asObject(kioskConfig.theme);

  const sellingPointId = text(kioskConfig.sellingPointId || terminal.sellingPointId);
  const terminalId = text(kioskConfig.terminalId || terminal.id);
  // The current Qommerce touch bootstrap resolves a selling point (or a QR
  // token). Keep the physical terminal UUID as terminalId, but use the scoped
  // selling point identifier for bootstrap until terminal lookup is canonical.
  const bootstrapIdentifier = sellingPointId || text(kioskConfig.connectionUuid || terminal.connectionUuid);
  const configuredMode = text(kioskConfig.deviceMode || config.appSetupType).toLowerCase();
  const deviceMode = configuredMode === "table" ? "table" : "kiosk";
  const tableCode = text(kioskConfig.tableCode || kioskConfig.serviceLocationCode);
  const host = text(tenant.host).toLowerCase();

  if (!bootstrapIdentifier || !sellingPointId || !terminalId || !host) {
    throw new Error("DOMAIN_RUNTIME_CONFIG_INCOMPLETE");
  }
  if (deviceMode === "table" && !tableCode) {
    throw new Error("DOMAIN_TABLE_CODE_REQUIRED");
  }

  localStorage.setItem("connectionUuid", bootstrapIdentifier);
  localStorage.setItem("sellingPointId", sellingPointId);
  localStorage.setItem("terminalId", terminalId);
  localStorage.setItem("terminalConfig", JSON.stringify(kioskConfig));
  localStorage.setItem("consumerRuntimePublishedTerminalConfig", JSON.stringify(kioskConfig));
  persistAndApplyTerminalTheme(kioskConfig);
  localStorage.setItem("consumerRuntimeDeviceMode", deviceMode);
  localStorage.setItem("consumerRuntimeMarketDomain", host);
  const platformName = text(masterdata.platformName || kioskConfig.restaurantName || host);
  const platformCode = text(tenant.platformCode || masterdata.platformCode);
  const snapshotUrl = `${CDN_ORIGIN}/domains/${encodeURIComponent(host)}/versions/${encodeURIComponent(text(snapshot.version))}/full.json`;
  localStorage.setItem("consumerRuntimePlatformMetadata", JSON.stringify({
    sourceUrl: snapshotUrl,
    raw: masterdata,
    platformCode,
    platformName,
    appName: platformName,
    description: "",
    supportEmail: "",
    publicDomain: host,
    appDomain: host,
    dashboardDomain: "",
    iconUrl: "",
    logoLightUrl: "",
    logoDarkUrl: "",
    primaryColor: text(theme.buttonColor) || "#f97316",
    secondaryColor: text(theme.background) || "#111827",
  }));
  localStorage.setItem("platformName", platformName);
  localStorage.setItem("platformAppName", platformName);
  localStorage.setItem("platformCode", platformCode);
  if (tableCode) localStorage.setItem("consumerRuntimeTableCode", tableCode);
  else localStorage.removeItem("consumerRuntimeTableCode");
};

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value as JsonObject)
      .sort()
      .map((key) => [key, stableValue((value as JsonObject)[key])])
  );
};

const sha256 = async (value: unknown): Promise<string> => {
  const bytes = new TextEncoder().encode(JSON.stringify(stableValue(value), null, 2));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const fetchJson = async (url: string): Promise<unknown> => {
  const response = await fetch(url, {
    headers: { accept: "application/json" },
    cache: "no-cache",
  });
  if (!response.ok) throw new Error(response.status === 404 ? "DOMAIN_NOT_PUBLISHED" : "DOMAIN_CDN_UNAVAILABLE");
  return response.json();
};

const loadMarketSurfaceRuntime = async (): Promise<DomainRuntimeSnapshot | null> => {
  const context = asObject(await fetchJson("/consumer-api/v1/context"));
  const marketId = text(context.marketplaceId);
  if (!marketId) throw new Error("MARKET_CONTEXT_INVALID");
  const manifest = asObject(await fetchJson(`/market-runtimes/v1/${encodeURIComponent(marketId)}/surfaces/kiosk/manifest.json`));
  const runtimeRef = asObject(manifest.runtime);
  const runtimePath = text(runtimeRef.path);
  if (!runtimePath.startsWith(`/market-runtimes/v1/${marketId}/surfaces/kiosk/versions/`) || !runtimePath.endsWith("/runtime.json")) {
    throw new Error("MARKET_RUNTIME_MANIFEST_INVALID");
  }
  const runtime = asObject(await fetchJson(runtimePath));
  if (runtime.schemaVersion !== MARKET_RUNTIME_SCHEMA || text(runtime.surface) !== "kiosk") {
    throw new Error("MARKET_RUNTIME_INVALID");
  }
  const market = asObject(runtime.market);
  if (text(market.id) !== marketId) throw new Error("MARKET_RUNTIME_INVALID");
  return {
    schemaVersion: SNAPSHOT_SCHEMA,
    version: text(manifest.version),
    tenant: { host: window.location.hostname, marketId },
    masterdata: { platformName: text(market.name), platformCode: text(market.code) },
    config: asObject(runtime.config),
    translations: asObject(runtime.translations),
  } as DomainRuntimeSnapshot;
};

export const loadCurrentDomainRuntime = async (): Promise<DomainRuntimeSnapshot | null> => {
  if (import.meta.env.DEV) return null;

  try {
    const marketRuntime = await loadMarketSurfaceRuntime();
    if (marketRuntime) {
      localStorage.setItem("consumerRuntimeDomainSnapshot", JSON.stringify(marketRuntime));
      applyDomainRuntimeSnapshot(marketRuntime);
      return marketRuntime;
    }
  } catch (error) {
    console.warn("Market-scoped kiosk runtime unavailable; trying legacy host snapshot", error);
  }

  const host = window.location.hostname.trim().toLowerCase();
  if (!host) throw new Error("DOMAIN_REQUIRED");

  const domainRoot = `${CDN_ORIGIN}/domains/${encodeURIComponent(host)}`;
  const manifest = asObject(await fetchJson(`${domainRoot}/manifest.json`));
  const full = asObject(manifest.full);
  const fullUrl = String(full.url || "").trim();
  const expectedPrefix = `${domainRoot}/versions/`;

  if (
    manifest.schemaVersion !== MANIFEST_SCHEMA ||
    String(manifest.host || "").trim().toLowerCase() !== host ||
    full.schemaVersion !== SNAPSHOT_SCHEMA ||
    !fullUrl.startsWith(expectedPrefix) ||
    !fullUrl.endsWith("/full.json")
  ) {
    throw new Error("DOMAIN_MANIFEST_INVALID");
  }

  const snapshot = asObject(await fetchJson(fullUrl)) as DomainRuntimeSnapshot;
  const tenant = asObject(snapshot.tenant);
  if (
    snapshot.schemaVersion !== SNAPSHOT_SCHEMA ||
    String(tenant.host || "").trim().toLowerCase() !== host ||
    String(snapshot.version || "") !== String(manifest.version || "") ||
    await sha256(snapshot) !== String(manifest.sha256 || "")
  ) {
    throw new Error("DOMAIN_SNAPSHOT_INVALID");
  }

  localStorage.setItem("consumerRuntimeDomainSnapshot", JSON.stringify(snapshot));
  applyDomainRuntimeSnapshot(snapshot);
  return snapshot;
};
